import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { IOrder, ISamplePool, Sample } from '../../../Model/index.js'
import { Environment } from '../../../environment.js'
import { tracingAxios } from '../../../Repository/Sentry.js'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../Flux/index.js'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import { OrderSearchWidget } from '../../Component/OrderSearchWidget.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import type { Taxas } from '../../../Model/ITaxa.js'
import { importableMessage, projectSampleIdSet, sampleIdsToAdd, summariseImportable } from './ImportSamplesToProjectModal.logic.js'
import { selectCurrentSamples, selectSamplesLoading } from '../../../Flux/Selector/index.js'

export interface ImportSamplesToProjectModalProps {
  project: IEvaluationProject
}

export const ImportSamplesToProjectModal: React.FC<ImportSamplesToProjectModalProps & BaseModalProps> = (props) => {
  const [samplePools, setSamplePools] = useState<ISamplePool[] | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null)
  const [selectedSamples, setSelectedSamples] = useState<Record<string, Sample>>({})
  const [error, setError] = useState<string | undefined>()
  const dispatch = useDispatch()
  const [stepId, setStepId] = useState<number>(0)

  const [samplesWithoutOtus, setSamplesWithoutOtus] = useState<number>(0)
  const [canSubmit, setCanSubmit] = useState<boolean>(false)

  const currentSamples = useSelector(selectCurrentSamples)
  const samplesLoading = useSelector(selectSamplesLoading) || currentSamples === 'LOADING'
  const projectSampleIds = useMemo(() => projectSampleIdSet(currentSamples), [currentSamples])
  const isImportable = (sample: Sample) => !projectSampleIds.has(sample.id)
  const importable = useMemo(
    () => (samplePools ? summariseImportable(samplePools, projectSampleIds) : undefined),
    [samplePools, projectSampleIds]
  )

  useEffect(() => {
    setSamplesWithoutOtus(Object.values(selectedSamples).filter((x) => !x.otuCount || x.otuCount === 0).length)
  }, [selectedSamples])

  useEffect(() => {
    setSelectedSamples({})
  }, [samplePools])

  useEffect(() => {
    setCanSubmit(!samplesLoading && !!samplePools && !!Object.values(selectedSamples).find((x) => x))
  }, [samplesLoading, samplePools, selectedSamples])

  useEffect(() => {
    dispatch(Actions.order.getAllOrderRequest())
  }, [])

  const handleFetchPools = async (orderIdToImport: string) => {
    try {
      setError(undefined) // Clear any previous error
      setSelectedSamples({})
      const response = await tracingAxios.get<ISamplePool[]>(`${Environment.API_URL}/api/orders/${orderIdToImport}/pools`, {
        withCredentials: true
      })
      setSamplePools(response.data)
    } catch (err) {
      console.error('Error fetching sample pools:', err)
      setError('Failed to fetch sample pools. Please check the Order ID and try again.')
    }
  }

  const handleSelectAllSamplesFromAllPools = () => {
    if (!samplePools) return

    const allSamples: Record<string, Sample> = {}
    samplePools.forEach((pool) => {
      pool.samples
        .filter(isImportable)
        .forEach((sample) => {
          allSamples[sample.id] = sample
        })
    })
    setSelectedSamples(allSamples)
  }

  const handleImport = async () => {
    const sampleIds = sampleIdsToAdd(
      Object.keys(selectedSamples).filter((key) => selectedSamples[key]),
      projectSampleIds
    )
    if (sampleIds.length > 0) {
      dispatch(Actions[projectSlice.name].addProjectSamplesRequest({ projectId: props.project.id, samples: sampleIds }))
    }
    props.onClose()
  }

  const toBaseTaxa = (taxa?: Taxas) => {
    return taxa
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="modal-box max-w-full max-h-[80%]">
        <h3 className="text-lg font-bold">{'Add Samples from Order to Project (Samples already included are not displayed)'}</h3>
        <div className="flex justify-center">
          <ul className="steps">
            <li className={`cursor-pointer step ${stepId === 0 ? 'step-primary' : ''}`} onClick={() => setStepId(0)}>
              Select Order
            </li>
            <li
              className={`step ${stepId === 1 ? 'step-primary' : ''} ${samplePools ? 'cursor-pointer' : 'text-gray-300'}`}
              onClick={() => setStepId(samplePools ? 1 : 0)}
            >
              Select Samples
            </li>
          </ul>
        </div>
        {error && <div className="alert-error">{error}</div>}

        {stepId === 0 && (
          <div className="my-4">
            <div className="mb-4">
              <OrderSearchWidget
                onSelect={async (order) => {
                  setSelectedOrder(order)
                  await handleFetchPools(order.id)
                  setStepId(1)
                }}
                initialFilters={{
                  taxa: props.project.targetTaxonomy,
                  processingType: props.project.processingType,
                }}
              />
            </div>
          </div>
        )}

        {samplePools && stepId === 1 && (
          <div className="my-4 space-y-2 overflow-y-auto max-h-96">
            <div className="mb-4">
              <button className="btn btn-sm btn-primary mb-2" onClick={handleSelectAllSamplesFromAllPools}>
                Select All Samples from All Pools
              </button>
              <p className="text-sm text-gray-600">Or expand pools below to select specific samples</p>
            </div>
            {importable && (
              <div data-testid="importable-summary" className={importable.importable === 0 ? 'alert alert-info' : 'text-sm'}>
                {importableMessage(importable)}
              </div>
            )}

            {samplePools.map((pool) => (
              <div key={pool.id} tabIndex={0} className="collapse collapse-arrow border border-base-300 rounded-box">
                <input type="checkbox" />
                <div className="collapse-title text-lg font-medium">
                  Pool {pool.id} ({pool.samples.filter(isImportable).length} Samples)
                </div>

                <div className="collapse-content">
                  <div className="my-1 flex items-center space-x-2 ">
                    <span
                      onClick={() => {
                        setSelectedSamples((previous) => {
                          /* a tad complicated, but we want to select all samples in the current pool,
                          which have not been imported before.
                          we need to work around react state handling a bit so we finally expand the result as a new object
                          */
                          const res = pool /* only samples which have not been selected */.samples
                            .filter(isImportable)
                            /* accumulate the samples by setting the keys in the selection mapping */
                            .reduce(
                              (acc, item) => {
                                acc[item.id] = item
                                return acc
                              },
                              {} as Record<string, Sample>
                            )
                          /* we only want to select additional samples, so keep previous mappings */
                          return { ...previous, ...res }
                        })
                      }}
                      className="underline cursor-pointer"
                    >
                      Select all Samples
                    </span>
                    <span
                      onClick={() => {
                        setSelectedSamples((previous) => {
                          /* convince react state to do the correct thing with copies */
                          const copy = { ...previous }
                          /* remove all samples from the current pool from the selection, but do not touch other
                           * samples */
                          pool.samples.reduce((acc, item) => {
                            delete acc[item.id]
                            return acc
                          }, copy)
                          return copy
                        })
                      }}
                      className="underline cursor-pointer"
                    >
                      Deselect all Samples
                    </span>
                  </div>

                  {pool.samples
                    .filter(isImportable)
                    .map((sample) => (
                      <div key={sample.id} className="flex items-center space-x-2 my-1">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={!!selectedSamples[sample.id]}
                          onChange={() =>
                            setSelectedSamples((previous) => {
                              if (previous[sample.id]) {
                                const changed = { ...previous }
                                delete changed[sample.id]
                                return changed
                              } else {
                                return { ...previous, [sample.id]: sample }
                              }
                            })
                          }
                        />
                        <span>
                          {sample.customerSampleName || sample.forwardFile?.fileName || 'N/A'} | {sample.otuCount} features | Forward:{' '}
                          {sample.forwardFile?.fileName || 'N/A'} | Reverse: {sample.reverseFile?.fileName || 'N/A'}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-action">
          {samplesLoading && <span className="inline-flex items-center italic">{"Loading the project's samples…"}</span>}
          {samplesWithoutOtus > 0 && (
            <span className="inline-flex items-center bg-warning text-white border-accent">
              There are {samplesWithoutOtus} samples without features selected
            </span>
          )}
          {selectedOrder &&
            props.project?.targetTaxonomy &&
            toBaseTaxa(props.project.targetTaxonomy) != toBaseTaxa(selectedOrder.targetTaxonomy) && (
              <span className="inline-flex items-center bg-warning text-white border-accent">
                Selected Order has a different target Taxonomy
              </span>
            )}
          {selectedOrder &&
            props.project?.processingType &&
            props.project.processingType !== selectedOrder.processingType && (
              <span className="inline-flex items-center bg-warning text-white border-accent">
                Selected Order has a different Processing Type ({selectedOrder.processingType})
              </span>
            )}
          <button onClick={handleImport} className={`btn btn-primary ${!canSubmit ? 'btn-disabled' : ''}`} disabled={!canSubmit}>
            Import {Object.values(selectedSamples).filter((x) => x).length} Samples
          </button>
          <button onClick={() => props.onClose()} className="btn btn-outline">
            Close
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
